import {alpha, SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/** The card every section on a detail page now sits in. It was a 4px panel with its own padding. */
export const detailsWrapperSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

export const sectionHeaderSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 2.5,
    // Deliberately shorter than the identity card's header — a section heading, not the page's.
    py: 2,
};

export const sectionBadgeSx: SxProps<Theme> = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '18px'},
};

export const sectionTitleSx: SxProps<Theme> = {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
};

/** The language tabs read as a band, the way the filter band does on a list. */
export const tabsBandSx: SxProps<Theme> = {
    px: 2.5,
    py: 0.5,
    background: theme.palette.neutral[50],
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    borderBottom: `1px solid ${theme.palette.neutral[100]}`,
};

/**
 * The three translations, stacked in one cell.
 *
 * The section is then as tall as the longest of them, whichever is on screen, so switching language
 * never moves what is below. Unmounting the inactive panels is what made the card resize.
 */
export const panelStackSx: SxProps<Theme> = {
    display: 'grid',
};

export const panelSx: SxProps<Theme> = {
    gridArea: '1 / 1',
};

/** Running text, given a measure. A description across the full width of a wide screen is unreadable. */
export const descriptionBodySx: SxProps<Theme> = {
    px: 2.5,
    py: 2.75,
    maxWidth: '70ch',
    // A floor, so a one-line description does not collapse the section to a strip under its tabs.
    minHeight: theme.spacing(14),
};
export const markdownSx: SxProps<Theme> = {
    '& ol li': {
        listStyle: 'auto',
    },
    '& ul li': {
        listStyle: 'disc',
    },
};
export const imageWrapperSx: SxProps<Theme> = {
    maxWidth: 215,
    alignItems: 'center',
    display: 'flex',
    position: 'relative',
    '& img': {
        width: '100%',
    },
};

export const detailItemSx: SxProps<Theme> = {
    '&.MuiGrid-root': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(8 / 8),
    },
};
export const buttonImageWrapperSx: SxProps<Theme> = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    padding: 'inherit',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    background: alpha(theme.palette.bg.dark, 0.2),
    transition: '0.3s ease',
    '&:hover': {
        opacity: 1,
    },
};
export const imageButtonSx: SxProps<Theme> = {
    '&:hover': {
        background: theme.palette.neutral[400],
    },
};
export const tabsSx: SxProps<Theme> = {
    minHeight: 0,
    '.MuiTabs-indicator': {
        height: '2px',
        background: theme.palette.primary.main,
    },
};

export const tabSx: SxProps<Theme> = {
    color: theme.palette.neutral[600],
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'capitalize',
    alignItems: 'flex-start',
    px: 0,
    py: 1.25,
    minHeight: 0,
    minWidth: 0,
    '&.Mui-selected': {
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
    '&:not(:last-child)': {
        mr: 3,
    },
};
